import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import messageTemplateService from '../../services/messageTemplateService';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';

const MAX_TEMPLATES = 10;

const TemplateManagementModal = ({ isOpen, onClose, onTemplateSelect }) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'general'
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen && user?.id) {
      loadTemplates();
    }
  }, [isOpen, user?.id]);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await messageTemplateService.getUserTemplates(user.id);
      
      if (result.success) {
        setTemplates(result.data);
      } else {
        setError(result.error || 'Failed to load templates');
      }
    } catch (err) {
      setError('An unexpected error occurred while loading templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    if (!formData.name.trim() || !formData.content.trim()) {
      setError('Template name and content are required');
      setIsSubmitting(false);
      return;
    }

    try {
      let result;
      if (editingTemplate) {
        result = await messageTemplateService.updateTemplate(
          editingTemplate.id,
          user.id,
          formData
        );
      } else {
        result = await messageTemplateService.createTemplate(user.id, formData);
      }

      if (result.success) {
        setSuccessMessage(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!');
        setFormData({ name: '', content: '', category: 'general' });
        setEditingTemplate(null);
        setIsEditing(false);
        await loadTemplates();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred while saving the template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category || 'general'
    });
    setIsEditing(true);
    setError('');
    setSuccessMessage('');
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(templateId);
    setError('');
    
    try {
      const result = await messageTemplateService.deleteTemplate(templateId, user.id);

      if (result.success) {
        setSuccessMessage('Template deleted successfully!');
        await loadTemplates();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred while deleting the template');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      // Increment usage count
      await messageTemplateService.incrementUsageCount(template.id, user.id);
      
      // Pass template to parent component
      if (onTemplateSelect) {
        onTemplateSelect(template);
      }
      
      onClose();
    } catch (err) {
      // Even if incrementing fails, still use the template
      if (onTemplateSelect) {
        onTemplateSelect(template);
      }
      onClose();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingTemplate(null);
    setFormData({ name: '', content: '', category: 'general' });
    setError('');
    setSuccessMessage('');
  };

  const handleClose = () => {
    handleCancel();
    setSearchQuery('');
    setSelectedCategory('all');
    onClose();
  };

  // Filter templates based on search and category
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.content.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (!isOpen) return null;

  const canAddMore = templates.length < MAX_TEMPLATES;
  const categories = messageTemplateService.getCategories();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Message Templates</h2>
            <p className="text-sm text-gray-600 mt-1">
              {templates.length} of {MAX_TEMPLATES} templates used
              {templates.length >= MAX_TEMPLATES && (
                <span className="text-amber-600 ml-2">• Limit reached</span>
              )}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            disabled={isSubmitting}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <Icon name="AlertCircle" size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <Icon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Add/Edit Form */}
          {(isEditing || canAddMore) && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Follow-up Message"
                    maxLength={100}
                    required
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.name.length}/100 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Content *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter your message template..."
                    rows={4}
                    maxLength={1000}
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.content.length}/1000 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex space-x-3">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="flex-1" 
                    disabled={isSubmitting || !formData.name.trim() || !formData.content.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {editingTemplate ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Icon name={editingTemplate ? "Save" : "Plus"} size={16} className="mr-2" />
                        {editingTemplate ? 'Update Template' : 'Create Template'}
                      </>
                    )}
                  </Button>
                  {(editingTemplate || isEditing) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Show button to add new template when not editing */}
          {!isEditing && canAddMore && templates.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="w-full mb-6"
              disabled={isSubmitting}
            >
              <Icon name="Plus" size={16} className="mr-2" />
              Add New Template
            </Button>
          )}

          {/* Template limit reached message */}
          {!canAddMore && !isEditing && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start space-x-2">
              <Icon name="AlertCircle" size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                You've reached the maximum of {MAX_TEMPLATES} templates. Delete an existing template to create a new one.
              </p>
            </div>
          )}

          {/* Search and Filter */}
          {templates.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  iconName="Search"
                />
              </div>
              <div className="sm:w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Templates List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Your Templates</h3>
              {searchQuery && (
                <p className="text-sm text-gray-500">
                  {filteredTemplates.length} of {templates.length} templates
                </p>
              )}
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading templates...</span>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Icon name="FileText" size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium">No templates yet</p>
                <p className="text-sm mt-1">Create your first template above to get started!</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Icon name="Search" size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium">No templates found</p>
                <p className="text-sm mt-1">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {template.name}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded capitalize">
                            {template.category}
                          </span>
                          {template.usage_count > 0 && (
                            <span className="text-xs text-gray-500">
                              Used {template.usage_count} {template.usage_count === 1 ? 'time' : 'times'}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            Created {new Date(template.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Use template"
                          disabled={isSubmitting}
                        >
                          <Icon name="MessageSquare" size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit template"
                          disabled={isSubmitting}
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete template"
                          disabled={isSubmitting || isDeleting === template.id}
                        >
                          {isDeleting === template.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Icon name="Trash2" size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {template.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button 
            variant="outline" 
            onClick={handleClose} 
            className="w-full"
            disabled={isSubmitting}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateManagementModal;